
import { Component, Vue, Watch, Prop, Emit } from "vue-property-decorator"

// @ts-ignore
import VueCal from "vue-cal"
import { Schedule, ScheduleFreq, CalendarEvent, MultipleRecordInstance, scheduleHelpers } from "@/types/schedule"
import { CaseSummary, TimeZone } from "@/types"

import moment from "moment"
import { DateTime } from "luxon"

import SelectModal from "@/components/Controls/SelectModal.vue"
import { DayOfWeek } from "@/types/view"
import { TenantFeatures } from "@/types/tenantFeatures"
import { Getter } from "vuex-class"
import { cloneDeep } from "lodash"

@Component({
	components: {
		"vue-cal": VueCal,
		"cal-overflow": SelectModal
	}
})
export default class CalendarView extends Vue {
	@Prop()
	schedules!: Schedule[]

	@Prop({ default: () => null })
	records!: CaseSummary[] | null

	@Prop()
	createNew!: (values: any) => void

	@Prop({ default: null })
	filter!: string | null

	@Prop({ default: false })
	hidePastSchedules!: boolean

	@Prop({ default: 15 })
	maxEventsPerDay!: number

	@Prop({ default: () => null })
	timeZones!: TimeZone[] | null

	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean

	get today () {
		return new Date()
	}

	overflowTitle: string | null = null
	overflowItems: { title: string }[] | null = null

	dateRange: {
		start: Date,
		end: Date
	} | null = null

	get filteredEvents (): CalendarEvent[] {
		if (!this.filter || this.filter === "") {
			return this.events
		}

		const filter = this.filter.toLowerCase()
		return this.events.filter(i => i.title.toLowerCase().includes(filter))
	}

	get events (): CalendarEvent[] {
		return [
			...this.scheduleEvents,
			...this.groupedRecordEvents
		]
	}

	get groupedRecordEvents (): CalendarEvent[] {
		// Group records by created (start)
		const grouped = this.recordEvents.reduce((eventGroups, record) => {
			const date = record.start instanceof Date ? (record.start as Date).toString() : (record.start as string)
			const group = eventGroups[date]

			if (group) {
				group.records.push(record)
			} else {
				eventGroups = {
					...eventGroups,
					[date]: {
						records: [record]
					}
				}
			}

			return eventGroups
		}, {} as { [key: string]: { records: CalendarEvent[] } })

		// Limit based on max length. If records are limited, show item to display popup
		return Object.keys(grouped).map(dateKey => {
			const group = grouped[dateKey]
			if (this.maxEventsPerDay > 0 && group.records.length > this.maxEventsPerDay) {
				return [
					...group.records.slice(0, this.maxEventsPerDay),
					{
						start: dateKey,
						end: dateKey,
						title: `And ${group.records.length - this.maxEventsPerDay} more`,
						allDay: true,
						records: group.records.slice(this.maxEventsPerDay),
						class: "m-t-10"
					}
				]
			} else {
				return group.records
			}
		}).flat()
	}

	get recordEvents (): CalendarEvent[] {
		if (!this.records) {
			return []
		}

		return this.records.map(record => {
			const recordCreated = new Date(record.created!)
			const dateString = recordCreated.getFullYear() + "-" + (recordCreated.getMonth() + 1) + "-" + recordCreated.getDate()
			return {
				start: dateString,
				end: dateString,
				title: record.title,
				allDay: true,
				record
			}
		})
	}

	get scheduleEvents (): CalendarEvent[] {
		if (!this.schedules || !this.dateRange) {
			return []
		}

		const viewDays: Date[] = []
		let date = new Date(this.dateRange.start)
		if (this.hidePastSchedules) {
			date = moment().utc().startOf("day").toDate()

			if (this.dateRange.end < date) {
				// Whole range is in the past
				return []
			}
		}

		while (date <= this.dateRange.end) {
			viewDays.push(new Date(date))
			date = this.addDays(date, 1) // Other methods don't seem to take account of day light saving time in all instances
		}

		const today = new Date()
		const todayDateString = today.getFullYear() + "-" + (today.getMonth() + 1) + "-" + today.getDate()

		let scheduleEvents = this.schedules.map(schedule => {
			switch (schedule.scheduleFreq) {
				case ScheduleFreq.Daily:
					return viewDays
						.filter(date => this.isInScheduleMaxMinDates(schedule, date) && !this.isOnExcludedDate(schedule, date))
						.map(date => {
							const dateString = date.getFullYear() + "-" + (date.getMonth() + 1) + "-" + date.getDate()
							return {
								start: dateString,
								end: dateString,
								title: schedule.title,
								allDay: true,
								hide: this.hidePastSchedules && (dateString === todayDateString) && this.hasPassed(date, schedule.startTime, schedule.timeZoneId),
								class: schedule.bulkCreateForTeams && schedule.bulkCreateForTeams.length > 0 ? "calendarview-bulk-schedule" : "",
								schedule
							}
						}).filter(sch => !sch.hide)
				case ScheduleFreq.Weekly:
					return viewDays
						.filter(date => date.getDay() === schedule.dayOf &&
							this.isInScheduleMaxMinDates(schedule, date) && !this.isOnExcludedDate(schedule, date))
						.map(date => {
							const dateString = date.getFullYear() + "-" + (date.getMonth() + 1) + "-" + date.getDate()
							return {
								start: dateString,
								end: dateString,
								title: schedule.title,
								allDay: true,
								hide: this.hidePastSchedules && dateString === todayDateString && this.hasPassed(date, schedule.startTime, schedule.timeZoneId),
								class: schedule.bulkCreateForTeams && schedule.bulkCreateForTeams.length > 0 ? "calendarview-bulk-schedule" : "",
								schedule
							}
						}).filter(sch => !sch.hide)
				case ScheduleFreq.Monthly:
					return this.scheduleMonthly(schedule, viewDays, todayDateString)
				case ScheduleFreq.Quarterly:
					// Remove non valid months
					var days = viewDays.filter(date => scheduleHelpers.isOnQuarterlyDate(schedule, date.getMonth() + 1))
					return this.scheduleMonthly(schedule, days, todayDateString)
				case ScheduleFreq.Once:
					if (schedule.date) {
						let date = new Date(schedule.date)

						// Create as a local date
						date = new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())

						if (this.hidePastSchedules && date < new Date()) {
							return []
						}

						if (this.isOnExcludedDate(schedule, date)) {
							return []
						}

						if (date >= this.dateRange!.start && date <= this.dateRange!.end) {
							const dateString = date.getFullYear() + "-" + (date.getMonth() + 1) + "-" + date.getDate()
							if (this.hidePastSchedules && dateString === todayDateString && this.hasPassed(date, schedule.startTime, schedule.timeZoneId)) {
								return []
							}

							return [
								{
									start: dateString,
									end: dateString,
									title: schedule.title,
									allDay: true,
									class: schedule.bulkCreateForTeams && schedule.bulkCreateForTeams.length > 0 ? "calendarview-bulk-schedule" : "",
									schedule
								}
							]
						}
					}
					break
				case ScheduleFreq.CustomDates:
					if (schedule.dates) {
						let timeObjectList: number[] = []

						// Convert all dates to times for easier comparison
						schedule.dates!.forEach(customDate => {
							timeObjectList.push(new Date(customDate).getTime())
						})

						return viewDays
							.filter(date => this.isInScheduleCustomDates(timeObjectList, date))
							.map(date => {
								const dateString = date.getFullYear() + "-" + (date.getMonth() + 1) + "-" + date.getDate()
								return {
									start: dateString,
									end: dateString,
									title: schedule.title,
									allDay: true,
									hide: this.hidePastSchedules && dateString === todayDateString && this.hasPassed(date, schedule.startTime, schedule.timeZoneId),
									class: schedule.bulkCreateForTeams && schedule.bulkCreateForTeams.length > 0 ? "calendarview-bulk-schedule" : "",
									schedule
								}
							}).filter(sch => !sch.hide)
					}
			}
			return []
		}).flat()

		// Duplicate events and change times for multi create schedules
		scheduleEvents.filter(e => e.schedule.recordsToCreate > 1 && !!e.schedule.multipleRecordInstances).forEach(e => {
			e.schedule.multipleRecordInstances.forEach((time: MultipleRecordInstance, index: number) => {
				// Skip the first item so we're not duplicated events
				if (index !== 0) {
					let newEvent = cloneDeep(e)
					newEvent.startTime = time.startTime

					scheduleEvents.push(newEvent)
				}
			})
		})

		return scheduleEvents
	}

	scheduleMonthly (schedule: Schedule, viewDays: Date[], todayDateString: string) {
		let dayOfMonth = schedule.dayOf!
		let days = viewDays.filter(date => date.getDate() === dayOfMonth)

		while (days.length === 0 && dayOfMonth > 27) {
			dayOfMonth--
			days = viewDays.filter(date => date.getDate() === dayOfMonth)
		}

		days.filter(date => this.isOnExcludedDate(schedule, date)).forEach(date => {
			// Remove old date that was on the weekend
			days.remove(date)

			let newDate: Date = new Date(date)
			if (date.getDay() === DayOfWeek.Saturday) {
				newDate.setDate(date.getDate() + 2)
			} else if (date.getDay() === DayOfWeek.Sunday) {
				newDate.setDate(date.getDate() + 1)
			}

			// Add new Date that is on the monday after the weekend
			days.push(newDate)
		})

		days = days.filter(date => this.isInScheduleMaxMinDates(schedule, date))

		let schedulesDates: any[] = days.map(date => {
			const dateString = date.getFullYear() + "-" + (date.getMonth() + 1) + "-" + date.getDate()
			return {
				start: dateString,
				end: dateString,
				title: schedule.title,
				allDay: true,
				hide: this.hidePastSchedules && dateString === todayDateString && this.hasPassed(date, schedule.startTime, schedule.timeZoneId),
				class: schedule.bulkCreateForTeams && schedule.bulkCreateForTeams.length > 0 ? "calendarview-bulk-schedule" : "",
				schedule
			}
		}).filter(sch => !sch.hide)

		return schedulesDates
	}

	isInScheduleCustomDates (timeObjectList: number[], calendarDate: Date): boolean {
		const executionDate = new Date(Date.UTC(calendarDate.getFullYear(),
			calendarDate.getMonth(),
			calendarDate.getDate()))

		// Check if the provided date is in the list of schedule dates (converted to time objects)
		if (timeObjectList.includes(executionDate.getTime())) {
			return true
		}
		return false
	}

	isInScheduleMaxMinDates (schedule: Schedule, calendarDate: Date): boolean {
		const executionDate = new Date(Date.UTC(calendarDate.getFullYear(),
			calendarDate.getMonth(),
			calendarDate.getDate()))

		if (schedule.minRunDate) {
			const minRunDate = new Date((typeof schedule.minRunDate === "string"
				? new Date(schedule.minRunDate)
				: schedule.minRunDate))

			if (executionDate < minRunDate) {
				return false
			}
		}

		if (schedule.maxRunDate) {
			const maxRunDate = new Date((typeof schedule.maxRunDate === "string" ? new Date(schedule.maxRunDate) : schedule.maxRunDate))

			if (executionDate > maxRunDate) {
				return false
			}
		}

		return true
	}

	@Emit("rangeChanged")
	viewChanged ({ startDate, endDate }: { startDate: Date, endDate: Date }) {
		this.dateRange = {
			start: new Date(startDate.getTime() - startDate.getTimezoneOffset() * 60000), // strips offset as date with offset that comes back from vue-cal is wrong, e.g. BST midnight should be 01:00 + GMT 1:00 but comes back as 00:00 + GMT 1:00. Doing this fixes it back to 01:00 + GMT 1:00
			end: new Date(endDate.getTime() - endDate.getTimezoneOffset() * 60000)
		}
		return this.dateRange
	}

	eventClicked (event: any, clickEvent: MouseEvent) {
		if (event.schedule) {
			this.$emit("scheduleClicked", event.schedule, event.start)
		} else if (event.record) {
			this.$emit("recordClicked", event.record)
		} else if (event.records) {
			this.overflowTitle = moment(event.start).format("L")
			this.overflowItems = event.records
		}

		if (clickEvent) clickEvent.stopPropagation()
	}

	cellClicked (date: Date) {
		this.createNew({
			scheduleFreq: ScheduleFreq.Once,
			date: date.toISOString()
		})
	}

	hasPassed (date: Date, startTime: string, timeZone: string): boolean {
		if (!this.timeZones || !startTime) return false
		const ianaTimeZone = this.timeZones.find(tz => tz.id === timeZone)
		if (!ianaTimeZone) return false

		const scheduleDate = new Date((typeof date === "string" ? new Date(date) : date))
		const [hour, minute] = startTime.split(":")

		if (hour === undefined || minute === undefined) {
			return false
		}

		const schedTime = DateTime.fromObject({
			year: scheduleDate.getFullYear(),
			month: scheduleDate.getMonth() + 1,
			day: scheduleDate.getDate(),
			hour: parseInt(hour),
			minute: parseInt(minute),
			zone: ianaTimeZone.iana
		})
		const now = DateTime.now().setZone(ianaTimeZone.iana)

		return schedTime < now
	}

	private addDays (date: Date, days: number) {
		var result = new Date(date)
		result.setTime(result.getTime() + days * 24 * 3600 * 1000)
		return result
	}

	// Returns true if the scheduled date should be excluded
	private isOnExcludedDate (schedule: Schedule, date: Date): boolean {
		if (this.getIsFeatureEnabled(TenantFeatures.SchedulingExcludeWeekends) && schedule.excludeWeekends) {
			if (date.getDay() === DayOfWeek.Saturday || date.getDay() === DayOfWeek.Sunday) {
				return true
			}
		}
		return false
	}
}

